import type { OtpContext } from '../types';

export type SendOtpCodeResponse = { success: boolean; message?: string };
export type VerifyOtpCodeResponse = {
  idpToken: string;
  email: string;
  context?: OtpContext;
};
export type LoginWithOtpResponse = { signupRequired?: boolean };

// Error types for specific error handling
export enum OtpErrorType {
  RATE_LIMIT = 'rate_limit',
  INVALID_CODE = 'invalid_code',
  EXPIRED_CODE = 'expired_code',
  MAX_ATTEMPTS = 'max_attempts',
  TOKEN_EXPIRED = 'token_expired',
  NETWORK_ERROR = 'network_error',
  UNKNOWN = 'unknown',
}

export class OtpError extends Error {
  type: OtpErrorType;
  cooldownSeconds?: number;

  constructor(message: string, type: OtpErrorType, cooldownSeconds?: number) {
    super(message);
    this.name = 'OtpError';
    this.type = type;
    this.cooldownSeconds = cooldownSeconds;
  }
}

// Helper to parse error responses and determine error type
const parseErrorResponse = async (response: Response): Promise<OtpError> => {
  try {
    const error = await response.json();
    const message = error.message || 'An error occurred';

    // Rate limiting (429 or specific message patterns)
    if (response.status === 429 || message.toLowerCase().includes('rate limit')) {
      // Try to extract cooldown seconds from message
      const match = message.match(/(\d+)\s*seconds?/i);
      const cooldownSeconds = match ? parseInt(match[1], 10) : 60;
      return new OtpError(
        message.includes('seconds')
          ? message
          : `Please wait ${cooldownSeconds} seconds before trying again`,
        OtpErrorType.RATE_LIMIT,
        cooldownSeconds
      );
    }

    // Expired code
    if (message.toLowerCase().includes('expired')) {
      return new OtpError(
        'This code has expired. Please request a new one.',
        OtpErrorType.EXPIRED_CODE
      );
    }

    // Max attempts exceeded
    if (
      message.toLowerCase().includes('max attempts') ||
      message.toLowerCase().includes('too many attempts') ||
      message.toLowerCase().includes('maximum')
    ) {
      return new OtpError(
        'Too many incorrect attempts. Please request a new code.',
        OtpErrorType.MAX_ATTEMPTS
      );
    }

    // Invalid code
    if (
      message.toLowerCase().includes('invalid') ||
      message.toLowerCase().includes('incorrect') ||
      response.status === 400
    ) {
      return new OtpError('Invalid code. Please check and try again.', OtpErrorType.INVALID_CODE);
    }

    // Token expired
    if (message.toLowerCase().includes('token') && message.toLowerCase().includes('expired')) {
      return new OtpError(
        'Your session has expired. Please start again.',
        OtpErrorType.TOKEN_EXPIRED
      );
    }

    // Generic error
    return new OtpError(message, OtpErrorType.UNKNOWN);
  } catch {
    // If we can't parse JSON, return a generic error
    return new OtpError(`Request failed with status ${response.status}`, OtpErrorType.UNKNOWN);
  }
};

// Helper to handle network errors
const handleNetworkError = (error: unknown): OtpError => {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return new OtpError(
      'Network error. Please check your connection and try again.',
      OtpErrorType.NETWORK_ERROR
    );
  }
  if (error instanceof OtpError) {
    return error;
  }
  return new OtpError(
    error instanceof Error ? error.message : 'An unexpected error occurred',
    OtpErrorType.UNKNOWN
  );
};

export const sendOtpCode = async (
  apiBaseUrl: string,
  email: string,
  context?: OtpContext
): Promise<SendOtpCodeResponse> => {
  try {
    const response = await fetch(`${apiBaseUrl}/api/auth/otp/send-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, context }),
    });

    if (!response.ok) {
      throw await parseErrorResponse(response);
    }

    return response.json();
  } catch (error) {
    throw handleNetworkError(error);
  }
};

export const verifyOtpCode = async (
  apiBaseUrl: string,
  email: string,
  code: string
): Promise<VerifyOtpCodeResponse> => {
  try {
    const response = await fetch(`${apiBaseUrl}/api/auth/otp/verify-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, code }),
    });

    if (!response.ok) {
      throw await parseErrorResponse(response);
    }

    return response.json();
  } catch (error) {
    throw handleNetworkError(error);
  }
};

export const loginWithOtp = async (
  apiBaseUrl: string,
  idpToken: string
): Promise<LoginWithOtpResponse> => {
  try {
    const response = await fetch(`${apiBaseUrl}/api/auth/otp/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ idpToken }),
    });

    if (!response.ok) {
      throw await parseErrorResponse(response);
    }

    const data = await response.json();
    return {
      signupRequired: data.status === 'signup_required',
    };
  } catch (error) {
    throw handleNetworkError(error);
  }
};

export const signupWithOtp = async (
  apiBaseUrl: string,
  idpToken: string,
  userData: { email: string; firstName: string; lastName: string }
): Promise<void> => {
  try {
    const response = await fetch(`${apiBaseUrl}/api/auth/otp/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ idpToken, ...userData }),
    });

    if (!response.ok) {
      throw await parseErrorResponse(response);
    }
  } catch (error) {
    throw handleNetworkError(error);
  }
};
